import {
  getFloatingPinyinKeyboardToggleFooterInset,
  getFloatingPinyinKeyboardToggleKeyboardSlotInset,
} from "@-/dev.improve.haniverse.routes.nav.app._components.pinyin-keyboard._layout";
import { cn } from "@-/com.npmjs.cn";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import Animated, {
  type AnimatedStyle,
  interpolateColor,
  useAnimatedStyle,
  createAnimatedComponent,
  useSharedValue,
  withTiming,
} from "@-/com.npmjs.react-native-reanimated";
import { useEffect, useMemo } from "react";
import { type ViewStyle, Pressable } from "@-/com.npmjs.react-native";
import { useTheme } from "@-/dev.improve.haniverse.lib.theme";
import { useCSSVariable } from "@-/com.npmjs.uniwind";

const ANIMATION_DURATION = 90;
const AnimatedPressable = createAnimatedComponent(Pressable);
const WHITESPACE_REGEX = /\s+/;

function hslaFromChannels(color: string | number | undefined, alpha = 1): string {
  if (color === undefined || color === "") {
    return `hsla(0, 0%, 0%, ${alpha})`;
  }

  const value = String(color);
  if (value.startsWith("hsla(")) {
    return value;
  }
  if (
    alpha === 1 &&
    (value.startsWith("hsl(") || value.startsWith("#") || value.startsWith("rgb"))
  ) {
    return value;
  }
  if (value.startsWith("#")) {
    const hex = value.slice(1);
    const normalized =
      hex.length === 3 ? Array.from(hex, (char) => char + char).join("") : hex.slice(0, 6);
    const r = Number.parseInt(normalized.slice(0, 2), 16);
    const g = Number.parseInt(normalized.slice(2, 4), 16);
    const b = Number.parseInt(normalized.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  if (value.startsWith("hsl(")) {
    const inner = value.slice(4, -1).trim();
    const [hue = "0", saturation = "0%", lightness = "0%"] = inner.split(WHITESPACE_REGEX);
    return `hsla(${hue}, ${saturation}, ${lightness}, ${alpha})`;
  }

  const [hue = "0", saturation = "0%", lightness = "0%"] = value.trim().split(WHITESPACE_REGEX);
  return `hsla(${hue}, ${saturation}, ${lightness}, ${alpha})`;
}

type FloatingPinyinKeyboardTogglePlacement = "footer" | "keyboard-slot";

type FloatingPinyinKeyboardToggleButtonProps = {
  animatedStyle?: AnimatedStyle<ViewStyle>;
  /**
   * Extra distance (px) to lift the control above its anchor's bottom edge. Use to seat the
   * button on the bottom key row when it is anchored to a container that also includes the
   * pinyin preview field below the keys.
   */
  bottomOffset?: number;
  className?: string;
  height: number;
  isActive: boolean;
  onPress: () => void;
  /** Where to anchor the absolutely positioned control. Defaults from `isActive`. */
  placement?: FloatingPinyinKeyboardTogglePlacement;
  width: number;
};

export function FloatingPinyinKeyboardToggleButton({
  animatedStyle,
  bottomOffset = 0,
  className,
  height,
  isActive,
  onPress,
  placement: placementProp,
  width,
}: FloatingPinyinKeyboardToggleButtonProps) {
  const placement = placementProp ?? (isActive ? "keyboard-slot" : "footer");
  const accessibilityLabel = isActive ? "Hide pinyin keyboard" : "Show pinyin keyboard";
  const colorScheme = useTheme();
  const backgroundChannels = useCSSVariable("--color-background");
  const foregroundChannels = useCSSVariable("--color-foreground");
  const primaryChannels = useCSSVariable("--color-primary");
  const progress = useSharedValue(isActive ? 1 : 0);

  const inactiveBackgroundColor = hslaFromChannels(backgroundChannels);
  const activeBackgroundColor = hslaFromChannels(
    primaryChannels,
    colorScheme === "dark" ? 0.2 : 0.15,
  );
  const inactiveBorderColor = hslaFromChannels(foregroundChannels, 0.15);
  const activeBorderColor = hslaFromChannels(primaryChannels);
  const iconClassName = isActive ? "text-primary" : "text-foreground";

  // The button keeps the same key-shaped rectangle whether or not the keyboard is
  // open: dismissing the keyboard slides it down behind a button that never moves
  // or changes shape.
  const displayWidth = width;
  const displayHeight = height;
  const iconSize = Math.round(Math.min(displayWidth, displayHeight) * 0.5);

  useEffect(() => {
    progress.set(withTiming(isActive ? 1 : 0, { duration: ANIMATION_DURATION }));
  }, [isActive, progress]);

  const colorStyle = useAnimatedStyle(() => ({
    backgroundColor: interpolateColor(
      progress.get(),
      [0, 1],
      [inactiveBackgroundColor, activeBackgroundColor],
    ),
    borderColor: interpolateColor(progress.get(), [0, 1], [inactiveBorderColor, activeBorderColor]),
  }));

  const positionInset = useMemo(
    () =>
      placement === "keyboard-slot"
        ? getFloatingPinyinKeyboardToggleKeyboardSlotInset()
        : getFloatingPinyinKeyboardToggleFooterInset(),
    [placement],
  );

  const pressableSizeStyle = useMemo(
    () => ({
      borderCurve: "continuous" as const,
      borderWidth: 1,
      elevation: 4,
      height: displayHeight,
      shadowOffset: { height: 2, width: 0 },
      shadowOpacity: 0.22,
      shadowRadius: 6,
      width: displayWidth,
    }),
    [displayHeight, displayWidth],
  );

  const pressableStyle = useMemo(
    () => [pressableSizeStyle, colorStyle],
    [colorStyle, pressableSizeStyle],
  );

  const wrapperStyle = useMemo(
    () => [
      {
        bottom: positionInset.bottom + bottomOffset,
        left: positionInset.left,
        position: "absolute" as const,
        zIndex: 60,
      },
      animatedStyle,
    ],
    [animatedStyle, bottomOffset, positionInset.bottom, positionInset.left],
  );

  return (
    <Animated.View className={cn(className)} pointerEvents="box-none" style={wrapperStyle}>
      <AnimatedPressable
        accessibilityLabel={accessibilityLabel}
        accessibilityRole="button"
        className="items-center justify-center overflow-hidden rounded-xs active:bg-accent"
        onPress={onPress}
        style={pressableStyle}
      >
        <Icon className={iconClassName} name="MsLanguageChinesePinyin" size={iconSize} />
      </AnimatedPressable>
    </Animated.View>
  );
}
